import { DarkTiles } from "@/components/map/DarkTiles";
import { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { MapPin, Search } from 'lucide-react';

// Fix for default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const DraggableMarker = ({ position, onPick }) => {
  useMapEvents({
    click(e) {
      const { lat, lng } = e.latlng;
      onPick(lat, lng, `${lat.toFixed(5)}, ${lng.toFixed(5)}`);
      fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`)
        .then(res => res.json())
        .then(data => data.display_name && onPick(lat, lng, data.display_name))
        .catch(err => console.error('Geocoding error:', err));
    },
  });

  return position ? <Marker position={position} /> : null;
};

const DAKAR = [14.6928, -17.4467];

export default function LocationPicker({ onLocationSelect, initialAddress = '', initialPosition = null }) {
  const iLat = initialPosition?.lat;
  const iLng = initialPosition?.lng;
  const [position, setPosition] = useState(iLat != null && iLng != null ? [iLat, iLng] : DAKAR);
  const [address, setAddress] = useState(initialAddress);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);

  // Suit la position fournie par le parent (ex. GPS arrivé après le montage)
  useEffect(() => {
    if (iLat != null && iLng != null) setPosition([iLat, iLng]);
  }, [iLat, iLng]);

  useEffect(() => {
    if (initialAddress) setAddress(initialAddress);
  }, [initialAddress]);

  // N'émet que sur action utilisateur : ne jamais écraser les coordonnées du parent au montage
  const pick = (lat, lng, addr) => {
    setPosition([lat, lng]);
    setAddress(addr);
    onLocationSelect?.({ position: [lat, lng], address: addr, lat, lng });
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;

    setIsSearching(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}&limit=1`
      );
      const data = await response.json();

      if (data && data.length > 0) {
        const { lat, lon, display_name } = data[0];
        pick(parseFloat(lat), parseFloat(lon), display_name);
      }
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setIsSearching(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <Input
          placeholder="Rechercher une adresse..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
          data-testid="location-search-input"
        />
        <Button
          onClick={handleSearch}
          disabled={isSearching}
          className="bg-primary hover:bg-primary/90"
          data-testid="location-search-btn"
        >
          <Search className="w-4 h-4" />
        </Button>
      </div>

      <div style={{ height: '350px', width: '100%', borderRadius: '0.75rem', overflow: 'hidden', border: '1px solid hsl(var(--border))' }}>
        <MapContainer
          center={position}
          zoom={13}
          style={{ height: '100%', width: '100%' }}
          key={position.join(',')}
        >
          <DarkTiles />
          <DraggableMarker
            position={position}
            onPick={pick}
          />
        </MapContainer>
      </div>

      {address && (
        <div className="flex items-start gap-2 p-3 bg-muted rounded-lg" data-testid="selected-address">
          <MapPin className="w-5 h-5 text-primary mt-0.5" />
          <div>
            <p className="text-sm font-medium">Adresse sélectionnée :</p>
            <p className="text-sm text-muted-foreground">{address}</p>
          </div>
        </div>
      )}
    </div>
  );
}
